// Google Analytics Configuration
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX'

// Initialize Google Analytics
export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return

  // Load gtag script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  // Initialize gtag
  window.dataLayer = window.dataLayer || []
  window.gtag = function() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    send_page_view: false, // We'll handle page views manually
  })
}

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  })
}

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', eventName, {
    event_category: 'engagement',
    event_label: eventName,
    ...parameters,
  })
}

// Legal Platform Specific Events
export const AnalyticsEvents = {
  // User Authentication
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  
  // Lawsuit Actions
  START_LAWSUIT_FILING: 'start_lawsuit_filing',
  COMPLETE_LAWSUIT_FILING: 'complete_lawsuit_filing',
  JOIN_LAWSUIT: 'join_lawsuit',
  BROWSE_LAWSUITS: 'browse_lawsuits',
  VIEW_LAWSUIT_DETAILS: 'view_lawsuit_details',
  STRENGTHEN_CASE: 'strengthen_case',
  
  // Document Generation
  GENERATE_DOCUMENTS: 'generate_documents',
  DOWNLOAD_DOCUMENT: 'download_document',
  
  // Payment Events
  INITIATE_PAYMENT: 'begin_checkout',
  COMPLETE_PAYMENT: 'purchase',
  PAYMENT_FAILED: 'payment_failed',
  
  // AI Interactions
  AI_CONSULTATION_START: 'ai_consultation_start',
  AI_CONSULTATION_COMPLETE: 'ai_consultation_complete',
  AI_DOCUMENT_GENERATION: 'ai_document_generation',
  
  // Navigation & Engagement
  SEARCH: 'search',
  SHARE: 'share',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  FAQ_VIEW: 'faq_view',
  PRICING_VIEW: 'pricing_view',
  
  // Language & Localization
  LANGUAGE_CHANGE: 'language_change',
  COUNTRY_SELECTION: 'country_selection',
  
  // Legal Resources
  VIEW_LEGAL_RESOURCES: 'view_legal_resources',
  DOWNLOAD_LEGAL_TEMPLATE: 'download_legal_template',
  
  // User Journey
  ONBOARDING_START: 'onboarding_start',
  ONBOARDING_COMPLETE: 'onboarding_complete',
  PROFILE_UPDATE: 'profile_update',
}

// Track lawsuit-specific events
export const trackLawsuitEvent = (action: string, lawsuitData?: {
  lawsuit_id?: string
  lawsuit_type?: string
  country?: string
  language?: string
  amount?: number
}) => {
  trackEvent(action, {
    event_category: 'lawsuit',
    ...lawsuitData,
  })
}

// Track user interactions
export const trackUserInteraction = (element: string, action: string, value?: string | number) => {
  trackEvent('user_interaction', {
    event_category: 'interaction',
    element_name: element,
    interaction_type: action,
    value: value,
  })
}

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean, errorMessage?: string) => {
  trackEvent(success ? 'form_submit_success' : 'form_submit_error', {
    event_category: 'form',
    form_name: formName,
    error_message: errorMessage,
  })
}

// Track search events
export const trackSearch = (searchTerm: string, searchCategory?: string, resultsCount?: number) => {
  trackEvent(AnalyticsEvents.SEARCH, {
    event_category: 'search',
    search_term: searchTerm,
    search_category: searchCategory,
    results_count: resultsCount,
  })
}

// Track payment events with e-commerce data
export const trackPayment = (action: 'begin_checkout' | 'purchase' | 'payment_failed', paymentData: {
  transaction_id?: string
  value: number
  currency: string
  payment_type?: string
  item_name?: string
  item_category?: string
}) => {
  if (action === 'purchase') {
    trackEvent('purchase', {
      event_category: 'ecommerce',
      transaction_id: paymentData.transaction_id,
      value: paymentData.value,
      currency: paymentData.currency,
      items: [{
        item_id: paymentData.transaction_id,
        item_name: paymentData.item_name,
        item_category: paymentData.item_category,
        price: paymentData.value,
        quantity: 1,
      }],
    })
  } else {
    trackEvent(action, {
      event_category: 'ecommerce',
      value: paymentData.value,
      currency: paymentData.currency,
      payment_type: paymentData.payment_type,
    })
  }
}

// Set user properties
export const setUserProperties = (properties: {
  user_id?: string
  user_type?: 'free' | 'premium' | 'admin'
  country?: string
  language?: string
  signup_date?: string
}) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: properties.user_id,
    custom_map: {
      user_type: properties.user_type,
      country: properties.country,
      language: properties.language,
    },
  })
}

// Track performance metrics
export const trackPerformance = (metricName: string, value: number, unit: string = 'ms') => {
  trackEvent('performance_metric', {
    event_category: 'performance',
    metric_name: metricName,
    metric_value: value,
    metric_unit: unit,
  })
}

// Track errors
export const trackError = (error: Error, errorInfo?: string, errorCategory?: string) => {
  trackEvent('exception', {
    event_category: 'error',
    description: error.message,
    fatal: false,
    error_info: errorInfo,
    error_category: errorCategory,
  })
}

// Initialize consent management (GDPR compliance)
export const updateConsentStatus = (granted: boolean) => {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
  })
}

// Type declarations for global gtag
declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}